/**
 * Adaptive performance budget — ONE shared system for every editor.
 *
 * A single <AdaptivePerfGovernor> (rendered inside each editor's <Canvas>) samples
 * the render framerate and tunes the scene's load so dense sections stay smooth.
 *
 * Knobs:
 *  - NOTES: a count budget (`maxNotes` per-mesh Console, `maxNotesVR` instanced VR)
 *    PLUS a draw distance `noteViewBeats`.
 *  - OBSTACLES: a draw distance `obstacleViewBeats` (the expensive FBX walls).
 *
 * A DRAW DISTANCE (beats ahead) is like a far-clip / FOV: shrinking it pulls the
 * horizon in (fewer objects on screen) while keeping EVERYTHING within it fully
 * visible — no gaps, no per-object popping; the FARTHEST objects drop off first.
 * Notes and obstacles have SEPARATE distances so each can be tuned independently.
 *
 * The framerate tuner cuts fast when frames drop and grows back slowly (with
 * hysteresis + cooldowns). A precomputed obstacle-density profile lets it also
 * shorten the horizon PROACTIVELY as a dense stretch approaches, before frames drop.
 * The debug HUD can pin either distance to a manual value.
 *
 * Plain module (not a store): the governor mutates `PERF` every frame with no
 * React re-renders; windowed components read `PERF` inside their per-frame loop.
 */

export interface PerfBudget {
  /** Smoothed framerate (exposed for the debug probe). */
  fps: number;
  /** Per-mesh (Console) note count cap. */
  maxNotes: number;
  /** Instanced VR note count cap (3 draw calls regardless — a high ceiling). */
  maxNotesVR: number;
  /** Draw distance for NOTES + rails, in beats ahead of the playhead. */
  noteViewBeats: number;
  /** Draw distance for OBSTACLES, in beats ahead of the playhead. */
  obstacleViewBeats: number;
  /** Hard safety cap on mounted obstacle count (crash guard only). */
  maxObstacles: number;
  /** 0..1 — overall headroom (for particle counts, shadow detail, etc.). */
  scale: number;
  /** Render-resolution multiplier (fraction of the base device pixel ratio).
   *  1 = native. Weak GPUs are usually FILL-bound: halving this quarters the
   *  fragments per frame, which recovers more than any object cap can. The
   *  governor lowers it only after the draw distance is already at minimum,
   *  and restores it FIRST when headroom returns (blur is more noticeable
   *  than a shorter horizon). */
  renderScale: number;
}

// Live per-category render counts for the debug HUD — each windower sets its own
// field every frame. `mounted` = Three objects in the tree; `visible` = actually
// drawn (after the FOV cull). A big gap means the cull is doing its job.
export const RENDER_STATS = {
  obsMounted: 0, obsVisible: 0, notes: 0, rails: 0,
};

export const PERF: PerfBudget = {
  fps: 60,
  maxNotes: 100,
  maxNotesVR: 448,
  noteViewBeats: 50,
  obstacleViewBeats: 50,
  maxObstacles: 300,
  scale: 1,
  renderScale: 1,
};

// Below LOW we're dropping frames → cut. Above HIGH we have headroom → grow.
const LOW_FPS = 46;
const HIGH_FPS = 57;

// ── Frame-rate cap awareness ────────────────────────────────────────────────
// Potato mode deliberately renders at a fixed 30fps. Two things then go wrong
// if the tuner isn't told:
//
//  1. The thresholds above are written for a 60fps target, so a PERFECT capped
//     30 reads as "dropping frames" and the tuner cuts forever.
//  2. The framerate the governor samples is the rAF delta, which keeps ticking
//     at the display's rate even on the frames the cap SKIPS — so it reports
//     ~60 while the user sees 30. Neither number is the truth on its own.
//
// So the frame limiter reports the rate it actually renders at, and the
// thresholds move to sit just under the cap. That turns the resolution ladder
// into a real dynamic-resolution controller: hold the cap → sharpen; miss it →
// soften. See perf/PotatoFrameCap.tsx.
let _fpsCap: number | null = null;
let _renderedFps: number | null = null;
// Highest rAF rate ever observed this session ≈ the display's refresh rate
// (rAF ticks at display rate even on frames a cap SKIPS). Used to clamp a
// user-set cap the display can't deliver: with fpsCap 120 on a 60Hz panel the
// render rate tops out at ~60, and thresholds at 116.5 would read that as
// permanent distress — the tuner would grind density to the floor and sink the
// ladder rung by rung. Deliberately the MAX ever seen, not the current rate:
// on a GPU-bound machine rAF itself slows, and following it down would relax
// the thresholds exactly when cuts are needed. Starts at 30 (every real
// display reaches that) so the first seconds aren't over-strict.
let _maxRafFps = 30;

/** Tell the tuner a hard frame-rate cap is in force (null = uncapped). */
export function setFpsCap(cap: number | null): void {
  _fpsCap = cap && cap > 0 ? cap : null;
  if (_fpsCap == null) _renderedFps = null;
}

/** The rate frames are actually being RENDERED at, when that differs from the
 *  rAF tick rate (i.e. while a cap is limiting the render pass). */
export function reportRenderedFps(fps: number | null): void {
  _renderedFps = fps;
}

/** Cut/grow thresholds for the current target. Under a cap, "good" is simply
 *  hitting the cap — with a small margin so a normal ±1fps wobble at the limit
 *  doesn't trip a cut. The cap is clamped to the display's observed refresh
 *  rate so an unreachable user cap can't become an unreachable target. */
function thresholds(): { low: number; high: number } {
  if (_fpsCap == null) return { low: LOW_FPS, high: HIGH_FPS };
  const target = Math.min(_fpsCap, _maxRafFps);
  return { low: target - 3.5, high: target - 1.2 };
}

const BOUNDS = {
  notes: { min: 24, max: 120 },
  notesVR: { min: 48, max: 512 },
  // Draw distance (beats ahead) for the ADAPTIVE horizon. Max = the scene's full
  // visible runway (VISIBLE_BEATS_AHEAD in TunnelScene). A manual override may go
  // outside this range; the auto tuner stays within it.
  view: { min: 14, max: 50 },
};

// Cut quickly to recover smoothness; grow cautiously so it doesn't see-saw.
const DOWN_COOLDOWN_MS = 400;
const UP_COOLDOWN_MS = 1600;
const CUT_FACTOR = 0.8;        // multiply the caps/horizon on a cut (~20% off)
const GROW_NOTES = 6;
const GROW_NOTES_VR = 14;
const GROW_VIEW = 4;           // extend the horizon per grow step (beats)

let _lastDown = 0;
let _lastUp = 0;

// Debug handle (console-only): the module's live internal state, so a "mode
// button does nothing" report can be split into store-didn't-update vs
// override-didn't-arrive vs tuner-didn't-apply in one read.
if (typeof window !== 'undefined') {
  (window as unknown as { __synapseBudget?: unknown }).__synapseBudget = {
    get state() {
      return {
        manualNoteView: _manualNoteView,
        manualObstacleView: _manualObstacleView,
        manualRenderScale: _manualRenderScale,
        adaptiveView: _adaptiveView,
        scaleIdx: _scaleIdx,
      };
    },
  };
}

// The shared adaptive horizon (beats), before per-type manual overrides.
let _adaptiveView = 50;

// Manual overrides (debug HUD): when non-null, force that type's draw distance
// independent of the adaptive result — for dialing in the FOV feel per type.
let _manualNoteView: number | null = null;
let _manualObstacleView: number | null = null;
export function setManualNoteView(v: number | null): void { _manualNoteView = v; }
export function setManualObstacleView(v: number | null): void { _manualObstacleView = v; }

// ── Render scale (subscribable) ─────────────────────────────────────────────
// Unlike the rest of PERF — which windowed components poll inside their own
// frame loop — the render scale has to reach REACT, because the only safe way
// to set the drawing-buffer resolution is the <Canvas dpr> prop itself.
//
// Why: R3F's `configure` runs on every Canvas render and does
//   `if (dpr && state.viewport.dpr !== calculateDpr(dpr)) setDpr(dpr)`
// so it re-asserts the prop over anything an imperative setDpr() did. The old
// governor fought that with a reconciler, and the two took turns resizing the
// drawing buffer — every reallocation blanks the canvas for a frame, which is
// the whole-editor FLASH users saw in Perf/Auto (and never in Quality, where
// the two values happened to agree). Now the prop is the single source of
// truth and nothing else touches the pixel ratio.
type ScaleListener = () => void;
const _scaleListeners = new Set<ScaleListener>();

export function subscribeRenderScale(listener: ScaleListener): () => void {
  _scaleListeners.add(listener);
  return () => { _scaleListeners.delete(listener); };
}
export function getRenderScale(): number { return PERF.renderScale; }

function applyRenderScale(v: number): void {
  if (Math.abs(v - PERF.renderScale) < 1e-4) return;
  PERF.renderScale = v;
  _scaleListeners.forEach(l => l());
}

// Render-resolution ladder. The tuner walks DOWN one rung at a time only when
// frames are still dropping with the draw distance already at its floor, and
// walks back UP before anything else once there's headroom. The 0.45 floor
// exists for GTX-980-class fill-bound GPUs that telemetry showed still under
// 50fps at 0.55 (≈5× fewer fragments than native at that rung).
/** The resolution rungs, sharpest first. Exported so the Performance settings
 *  panel and the benchmark offer exactly the rungs the ladder can land on. */
export const SCALE_STEPS = [1, 0.85, 0.7, 0.55, 0.45];
let _scaleIdx = 0;
// A rung change re-sizes the drawing buffer, which costs a frame — keep them
// rare and far apart so Auto can never see-saw between two rungs.
const RES_COOLDOWN_MS = 3000;
let _lastScaleStep = 0;

// Asymmetric hysteresis for the ladder. A cooldown alone is not enough: a
// machine sitting between two rungs (holds 30fps at 0.7, manages 23 at 0.85)
// would step up, fail, step down, recover, and step up again forever — a
// resolution change every few seconds, which is far more annoying than the
// lower rung. So a DOWN step also drops a CEILING: the rung that just failed
// is off-limits for a while. The ceiling expires, because "too slow" can be
// temporary (another app was hogging the GPU, a huge map was decoding).
const CEILING_MS = 60000;
let _ceilingIdx = 0;          // lowest index (= sharpest rung) currently allowed
let _ceilingUntil = 0;
/** Seed the ladder at the rung nearest `scale` — used when entering a mode that
 *  wants a specific starting sharpness but then lets the tuner take over. */
export function startRenderScaleAt(scale: number): void {
  let best = 0;
  for (let i = 1; i < SCALE_STEPS.length; i++) {
    if (Math.abs(SCALE_STEPS[i] - scale) < Math.abs(SCALE_STEPS[best] - scale)) best = i;
  }
  _scaleIdx = best;
  _lastScaleStep = 0;   // let the first adjustment happen without waiting out a cooldown
  _ceilingIdx = 0;
  _ceilingUntil = 0;
}

/** Manual render-scale override (PerfPill): non-null wins over the ladder. */
let _manualRenderScale: number | null = null;
export function setManualRenderScale(v: number | null): void {
  // Releasing an override (back to Auto): start the ladder from whatever the
  // pinned mode was showing, so Auto continues from the picture on screen
  // instead of snapping to a rung it drifted to while it was ignored.
  if (v == null && _manualRenderScale != null) {
    const pinned = _manualRenderScale;
    let best = 0;
    for (let i = 1; i < SCALE_STEPS.length; i++) {
      if (Math.abs(SCALE_STEPS[i] - pinned) < Math.abs(SCALE_STEPS[best] - pinned)) best = i;
    }
    _scaleIdx = best;
    // A ceiling learned under a different mode's target (potato's 30fps, say)
    // must not keep Auto blurry.
    _ceilingIdx = 0;
    _ceilingUntil = 0;
  }
  _manualRenderScale = v;
}

function recomputeScale() {
  const n = (PERF.maxNotes - BOUNDS.notes.min) / (BOUNDS.notes.max - BOUNDS.notes.min);
  const v = (_adaptiveView - BOUNDS.view.min) / (BOUNDS.view.max - BOUNDS.view.min);
  PERF.scale = Math.max(0, Math.min(1, (n + v) / 2));
}

/**
 * Feed one frame's smoothed FPS + the density-derived proactive horizon. Cuts/grows
 * the note budgets and the shared draw distance; then applies per-type manual
 * overrides (else the adaptive horizon) to the note + obstacle distances.
 */
export function tunePerfBudget(rafFps: number, now: number, proactiveView = BOUNDS.view.max): void {
  // Under a frame cap the rAF rate isn't what the user sees — the limiter's own
  // measured render rate is. Until that first report lands (~500ms), the rAF
  // rate would read as enormous headroom against the cap's thresholds and
  // trigger a step on nothing; better to leave the budget alone.
  if (_fpsCap != null && _renderedFps == null) return;
  // Track the display's refresh rate (max rAF rate ever seen) for the cap clamp
  // in thresholds(). The governor already filtered stalls before calling us.
  if (rafFps > _maxRafFps) _maxRafFps = rafFps;
  const fps = _renderedFps != null ? _renderedFps : rafFps;
  PERF.fps = fps;
  const { low: LOW, high: HIGH } = thresholds();

  // While a mode is pinned, the resolution ladder is frozen: the framerate it
  // would be reacting to is the PINNED picture's, so stepping now would just
  // leave a stale rung to snap to when the user goes back to Auto.
  const ladderFrozen = _manualRenderScale != null;
  // When BOTH draw distances are user-pinned (any non-Auto mode), the horizon
  // is not ours to shrink — resolution is the only lever left, so the ladder
  // must not wait for an adaptive horizon that will never move.
  const viewPinned = _manualNoteView != null && _manualObstacleView != null;

  if (fps < LOW) {
    if (now - _lastDown > DOWN_COOLDOWN_MS) {
      // Resolution drops only once the horizon is already at its floor — on a
      // strong GPU the object cuts alone recover 60fps and the picture stays
      // sharp; on a weak (fill-bound) GPU this rung is what actually saves it.
      if (!ladderFrozen && (viewPinned || _adaptiveView <= BOUNDS.view.min)
          && _scaleIdx < SCALE_STEPS.length - 1
          && now - _lastScaleStep > RES_COOLDOWN_MS) {
        _scaleIdx++;
        // The rung we just left could not hold the target — don't go back to it
        // for a while (see CEILING_MS).
        _ceilingIdx = _scaleIdx;
        _ceilingUntil = now + CEILING_MS;
        _lastScaleStep = now;
      }
      PERF.maxNotes = Math.max(BOUNDS.notes.min, Math.round(PERF.maxNotes * CUT_FACTOR));
      PERF.maxNotesVR = Math.max(BOUNDS.notesVR.min, Math.round(PERF.maxNotesVR * CUT_FACTOR));
      _adaptiveView = Math.max(BOUNDS.view.min, Math.round(_adaptiveView * CUT_FACTOR));
      _lastDown = now;
      _lastUp = now; // hold off growing right after a cut
    }
  } else if (fps > HIGH) {
    if (now - _lastUp > UP_COOLDOWN_MS) {
      if (now > _ceilingUntil) _ceilingIdx = 0;      // ceiling expired — free to try again
      if (!ladderFrozen && _scaleIdx > _ceilingIdx && now - _lastScaleStep > RES_COOLDOWN_MS) {
        _scaleIdx--;         // sharpen the picture back first
        _lastScaleStep = now;
      } else {
        PERF.maxNotes = Math.min(BOUNDS.notes.max, PERF.maxNotes + GROW_NOTES);
        PERF.maxNotesVR = Math.min(BOUNDS.notesVR.max, PERF.maxNotesVR + GROW_NOTES_VR);
        _adaptiveView = Math.min(BOUNDS.view.max, _adaptiveView + GROW_VIEW);
      }
      _lastUp = now;
    }
  }
  applyRenderScale(_manualRenderScale != null ? _manualRenderScale : SCALE_STEPS[_scaleIdx]);

  // Proactive horizon: a dense stretch just ahead shortens the distance now.
  if (_adaptiveView > proactiveView) _adaptiveView = Math.max(BOUNDS.view.min, proactiveView);

  // Per-type manual override wins; otherwise both follow the adaptive horizon.
  PERF.noteViewBeats = _manualNoteView != null ? _manualNoteView : _adaptiveView;
  PERF.obstacleViewBeats = _manualObstacleView != null ? _manualObstacleView : _adaptiveView;
  recomputeScale();
}

// ── Proactive density profile ───────────────────────────────────────────────
// Precomputed sorted beats of every obstacle mesh (set per difficulty). Lets the
// governor shorten the horizon as a dense stretch approaches — before frames drop.
let _obstacleBeats: number[] = [];

export function setObstacleDensity(sortedBeats: number[]): void {
  _obstacleBeats = sortedBeats;
}

/** How many obstacles fall in the beat window [from, to] (binary search). */
export function obstaclesInWindow(from: number, to: number): number {
  const a = _obstacleBeats;
  const n = a.length;
  if (!n) return 0;
  let lo = 0, hi = n;
  while (lo < hi) { const m = (lo + hi) >>> 1; if (a[m] < from) lo = m + 1; else hi = m; }
  const start = lo;
  lo = 0; hi = n;
  while (lo < hi) { const m = (lo + hi) >>> 1; if (a[m] <= to) lo = m + 1; else hi = m; }
  return lo - start;
}

// Comfortable number of obstacles to keep on screen. When the full-distance window
// would hold more, the proactive horizon shrinks so ~this many remain — the
// FARTHEST ones drop off (shorter draw distance), never the near field: no gaps.
const TARGET_OBSTACLES = 90;

/**
 * The draw distance (beats ahead) that keeps ~TARGET_OBSTACLES on screen given how
 * dense the runway ahead of `beat` is. Full distance when it's sparse.
 */
export function proactiveViewBeats(beat: number): number {
  const full = obstaclesInWindow(beat - 4, beat + BOUNDS.view.max);
  if (full <= TARGET_OBSTACLES) return BOUNDS.view.max;
  const scaled = BOUNDS.view.max * (TARGET_OBSTACLES / full);
  return Math.max(BOUNDS.view.min, Math.min(BOUNDS.view.max, scaled));
}
